import { useQuery } from "@tanstack/react-query";
import { Link, Navigate, useParams } from "react-router-dom";
import { Icon } from "@/components/Icon";
import { Seo } from "@/components/Seo";
import {
  JsonLd,
  articleSchema,
  breadcrumbSchema,
} from "@/components/StructuredData";
import { fetchKbArticles, type KbArticle } from "@/lib/kb-articles";
import { useContactEmail } from "@/lib/use-app-config";

export default function ArticlePage() {
  const { slug = "" } = useParams<{ slug: string }>();
  const { data: articles, isLoading } = useQuery({
    queryKey: ["kb-articles"],
    queryFn: fetchKbArticles,
    staleTime: 5 * 60 * 1000,
  });
  // Before the early returns below — hook order must not depend on load state.
  const contactEmail = useContactEmail();

  if (isLoading || !articles) {
    return (
      <main
        className="section"
        style={{
          minHeight: "60vh",
          display: "grid",
          placeItems: "center",
          color: "var(--on-surface-variant)",
        }}
      >
        Se încarcă…
      </main>
    );
  }

  const article = articles.find((a) => a.id === slug);
  if (!article) {
    return <Navigate to="/ajutor" replace />;
  }

  const idx = articles.findIndex((a) => a.id === article.id);
  const prev: KbArticle | null = idx > 0 ? articles[idx - 1] : null;
  const next: KbArticle | null =
    idx < articles.length - 1 ? articles[idx + 1] : null;

  return (
    <>
      <Seo
        title={`${article.title} — Asistent Auto`}
        description={article.snippet}
        path={`/ajutor/${article.id}`}
        type="article"
      />
      <JsonLd
        data={[
          articleSchema({
            title: article.title,
            description: article.snippet,
            path: `/ajutor/${article.id}`,
          }),
          breadcrumbSchema([
            { name: "Acasă", path: "/" },
            { name: "Informații utile", path: "/ajutor" },
            { name: article.title, path: `/ajutor/${article.id}` },
          ]),
        ]}
      />
      <header className="kb-article-hero">
        <div className="shell">
          <Link to="/ajutor" className="kb-back">
            <Icon name="arrow_back" />
            Toate articolele
          </Link>
          <div className="kb-tag-row">
            <span className="pill info">
              <Icon name={article.icon} />
              {article.tag}
            </span>
            {article.updated && (
              <span className="kb-updated">Actualizat {article.updated}</span>
            )}
          </div>
          <h1>{article.title}</h1>
        </div>
      </header>

      <section className="kb-article-body">
        <div className="shell">
          <div className="kb-intro">
            {article.intro.map((p, i) => (
              <p key={i} dangerouslySetInnerHTML={{ __html: p }} />
            ))}
          </div>

          {article.points.length > 0 && (
            <div className="kb-points">
              {article.points.map((p, i) => (
                <details
                  key={`${article.id}-${i}`}
                  className="kb-point"
                  open={i === 0}
                >
                  <summary>{p.q}</summary>
                  {p.intro && (
                    <p dangerouslySetInnerHTML={{ __html: p.intro }} />
                  )}
                  {p.bullets && p.bullets.length > 0 && (
                    <ul>
                      {p.bullets.map((b, j) => (
                        <li key={j}>
                          <span dangerouslySetInnerHTML={{ __html: b }} />
                        </li>
                      ))}
                    </ul>
                  )}
                </details>
              ))}
            </div>
          )}

          <div className="kb-article-cta">
            <div>
              <h3>Ai o întrebare specifică?</h3>
              <p>
                Sună sau scrie-ne — răspundem în aceeași zi în zilele
                lucrătoare.
              </p>
            </div>
            <a
              href={`mailto:${contactEmail}`}
              className="btn btn--primary"
            >
              <Icon name="mail" />
              Scrie-ne pe email
            </a>
          </div>

          <nav className="kb-pager">
            {prev ? (
              <Link to={`/ajutor/${prev.id}`} className="pager-prev">
                <span className="pager-dir">
                  <Icon name="arrow_back" />
                  Articol anterior
                </span>
                <span className="pager-title">{prev.title}</span>
              </Link>
            ) : (
              <Link to="/ajutor" className="pager-prev">
                <span className="pager-dir">
                  <Icon name="grid_view" />
                  Toate articolele
                </span>
                <span className="pager-title">Înapoi la listă</span>
              </Link>
            )}
            {next ? (
              <Link to={`/ajutor/${next.id}`} className="pager-next">
                <span className="pager-dir">
                  Articol următor
                  <Icon name="arrow_forward" />
                </span>
                <span className="pager-title">{next.title}</span>
              </Link>
            ) : (
              <Link to="/ajutor" className="pager-next">
                <span className="pager-dir">
                  Toate articolele
                  <Icon name="arrow_forward" />
                </span>
                <span className="pager-title">Înapoi la listă</span>
              </Link>
            )}
          </nav>
        </div>
      </section>
    </>
  );
}
